/** Test panel: evaluate the current (unsaved) block against a context and show the
 *  result. The user picks an event, then supplies a context one of three ways — pasted
 *  JSON, an Event_Delivery_Log id (its archived rendering context), or an entity id
 *  (GTU / order / user, per the event family). Optional channel + format selects pick
 *  which definition a template block renders (channel override → format definition →
 *  HTML (default)). Evaluation happens on the backend. */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  evaluateBlock,
  listSupportedEvents,
  BLOCK_FORMATS,
  OVERRIDE_CHANNELS,
  type BlockFormat,
  type EvaluateContext,
  type EvaluateResult,
  type NotificationBlockType,
  type NotificationBlockWrite,
  type OverrideChannel,
  type SupportedEvent,
} from '@/api/notificationBlocks';

type SourceMode = 'json' | 'event_delivery_log' | 'entity';

const ENTITY_LABEL: Record<'gtu' | 'order' | 'user', string> = {
  gtu: 'Tour participant (GTU) ID',
  order: 'Order ID',
  user: 'User ID',
};

interface Props {
  /** Reads the dialog's current form values into an (unsaved) block payload. */
  buildBlock: () => NotificationBlockWrite;
  /** The block type being edited. Channel + format only select a `template`
   *  block's definition, so they are hidden for every other type rather than
   *  offering controls that cannot change the result. */
  type?: NotificationBlockType;
}

export function BlockTestPanel({ buildBlock, type }: Props) {
  const showTarget = type === undefined || type === 'template';
  const { data: events = [] } = useQuery({
    queryKey: ['notification-block-events'],
    queryFn: listSupportedEvents,
    staleTime: 5 * 60_000,
  });

  const [event, setEvent] = useState<SupportedEvent | null>(null);
  const [mode, setMode] = useState<SourceMode>('json');
  const [jsonText, setJsonText] = useState('');
  const [edlId, setEdlId] = useState('');
  const [entityId, setEntityId] = useState('');
  // Delivery target for template blocks: '' = base evaluation (no channel).
  const [channel, setChannel] = useState<OverrideChannel | ''>('');
  const [format, setFormat] = useState<BlockFormat>('html');

  const entityKind = event?.entity_kind ?? null;
  const entitySupported = entityKind !== null;

  // Keep mode valid: entity is only available for events that support it.
  useEffect(() => {
    if (mode === 'entity' && !entitySupported) setMode('json');
  }, [mode, entitySupported]);

  const mutation = useMutation<EvaluateResult, unknown, void>({
    mutationFn: () => {
      let context: EvaluateContext;
      if (mode === 'event_delivery_log') context = { mode, event_delivery_log_id: edlId.trim() };
      else if (mode === 'entity') context = { mode, entity_id: Number(entityId) };
      else context = { mode: 'json', json: jsonText };
      return evaluateBlock({
        event: event?.code ?? '',
        block: buildBlock(),
        context,
        ...(channel ? { channel } : {}),
        format,
      });
    },
  });

  const result = mutation.data;
  const prettyResult = useMemo(() => {
    if (!result?.ok) return null;
    const v = result.result;
    if (v === null || v === undefined) return String(v);
    return typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v);
  }, [result]);

  const requestError =
    mutation.error instanceof ConsoleApiError
      ? mutation.error.message
      : mutation.error
        ? 'Evaluation request failed.'
        : null;

  const canEvaluate =
    mode === 'json' ||
    (mode === 'event_delivery_log' && edlId.trim() !== '') ||
    (mode === 'entity' && entitySupported && entityId.trim() !== '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Autocomplete<SupportedEvent>
        size="small"
        options={events}
        value={event}
        onChange={(_, v) => setEvent(v)}
        getOptionLabel={(o) => o.code}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        groupBy={(o) => o.family}
        renderInput={(params) => (
          <TextField {...params} label="Event" placeholder="Choose an event" />
        )}
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, v: SourceMode | null) => v && setMode(v)}
      >
        <ToggleButton value="json">Paste JSON</ToggleButton>
        <ToggleButton value="event_delivery_log">Delivery log</ToggleButton>
        <ToggleButton value="entity" disabled={!entitySupported}>
          {entityKind ? ENTITY_LABEL[entityKind].replace(/ ID$/, '') : 'Entity'}
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'json' && (
        <TextField
          label="Context JSON"
          size="small"
          fullWidth
          multiline
          minRows={5}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder={'{"order": {"ref": "ABC"}}'}
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
        />
      )}
      {mode === 'event_delivery_log' && (
        <TextField
          label="Event Delivery Log ID"
          size="small"
          fullWidth
          value={edlId}
          onChange={(e) => setEdlId(e.target.value)}
          placeholder="UUID — its archived rendering context is fetched"
        />
      )}
      {mode === 'entity' && entitySupported && (
        <TextField
          label={entityKind ? ENTITY_LABEL[entityKind] : 'Entity ID'}
          size="small"
          type="number"
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          helperText={`Builds the ${event?.family} context from this record.`}
        />
      )}

      {showTarget && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Channel"
              size="small"
              sx={{ width: 160 }}
              value={channel}
              onChange={(e) => setChannel(e.target.value as OverrideChannel | '')}
            >
              <MenuItem value="">None (base)</MenuItem>
              {OVERRIDE_CHANNELS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Format"
              size="small"
              sx={{ width: 180 }}
              value={format}
              onChange={(e) => setFormat(e.target.value as BlockFormat)}
            >
              {BLOCK_FORMATS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Channel + format pick which definition a template block renders: channel override →
            format definition → HTML (default).
          </Typography>
        </>
      )}
      {(type === 'rule' || type === 'tag') && (
        <Typography variant="caption" color="text.secondary">
          {type === 'rule'
            ? 'Rules read the event context, so pick an event and a real record for a meaningful verdict.'
            : 'A failing condition evaluates to an empty block — that is what templates fall through on.'}
        </Typography>
      )}

      <Box>
        <Button
          variant="outlined"
          size="small"
          disabled={!canEvaluate || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Evaluating…' : 'Evaluate'}
        </Button>
      </Box>

      {requestError && <Alert severity="error">{requestError}</Alert>}

      {result && !result.ok && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Evaluation error
          </Typography>
          <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {result.error}
          </Box>
          {result.result != null && (
            <Typography variant="caption">
              Fell back to default: {JSON.stringify(result.result)}
            </Typography>
          )}
        </Alert>
      )}

      {result?.ok && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Result
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              fontSize: 12,
            }}
          >
            {prettyResult}
          </Box>
        </Box>
      )}

      {result && result.context_keys.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Context namespaces: {result.context_keys.join(', ')}
        </Typography>
      )}
    </Box>
  );
}
